import AutorenewIcon from '@mui/icons-material/Autorenew'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import React from 'react'
import { Link } from 'react-router-dom'
import GaugeMeter from '../../../components/GaugeMeter'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { ChevronRightIcon } from '../../../template/kartera/icons'
import CustomerDrawer from '../../customers/drawer/customer-drawer'
import { formatCount, RamoMark } from '../common/summary-common'
import { SUMMARY_RAMOS, useSummaryDashboardModel } from './summary-dashboard-model'
import type { AgingView, BirthdayView } from './summary-dashboard-model'

const cardClass = 'flex items-center gap-3.5 rounded-2xl border border-k-line bg-k-surface px-5 py-4'

const AgingCard = ({ title, icon, to, linkLabel, enabled, state, buckets, noun }: { title: string, icon: React.ReactNode, to: string, linkLabel: string, enabled: boolean, state: object, buckets: AgingView[], noun: string }) => (
    <section className="k-card">
        <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-k-accent-soft text-k-accent-ink">{icon}</span>
                <h2 className="m-0 text-base font-extrabold text-k-ink">{title}</h2>
            </span>
            {enabled && (
                <Link to={to} state={state} className="inline-flex items-center gap-1 text-sm font-bold text-k-primary-ink hover:underline">
                    {linkLabel}
                    <ChevronRightIcon />
                </Link>
            )}
        </div>
        <div className="grid grid-cols-5 gap-2">
            {buckets.map((bucket) => (
                <div key={bucket.id} className={`flex flex-col items-center gap-0.5 rounded-xl px-1 py-2.5 ${bucket.tone}`}>
                    <span className="text-[20px] font-extrabold tabular-nums">{formatCount(bucket.count)}</span>
                    <span className="text-center text-[11px] font-bold leading-tight">{bucket.label}</span>
                </div>
            ))}
        </div>
        <p className="m-0 text-xs text-k-muted">{noun} por días para vencer.</p>
    </section>
)

const BirthdayPerson = ({ person, onOpen }: { person: BirthdayView, onOpen?: () => void }) => {
    const content = (
        <>
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold ${person.tone}`}>{person.initials}</span>
            <span className="max-w-[220px] truncate text-[13px] font-semibold text-k-ink-2" title={person.name}>{person.name}</span>
        </>
    )
    const className = 'inline-flex items-center gap-2 rounded-full border border-k-line-soft bg-k-sunken py-1 pl-1 pr-3 text-left'
    return onOpen
        ? <button type="button" onClick={onOpen} className={`${className} cursor-pointer hover:border-k-line-strong hover:bg-k-hover`}>{content}</button>
        : <span className={className}>{content}</span>
}

const SummaryDashboard = () => {
    const model = useSummaryDashboardModel()

    if (model.unsubscribed) {
        return (
            <KarteraAdminTemplate title="Resumen">
                <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-[18px]">
                    <KarteraPageHeader title={`Hola, ${model.name}`} description="Tu suscripción no está activa. Contrata un plan para volver a operar tu cartera." />
                    <section className="k-card border-k-danger-soft bg-k-danger-soft">
                        <h2 className="k-card-title text-k-danger-ink">Suscripción vencida</h2>
                        <p className="m-0 text-sm text-k-danger-ink">Puedes revisar tu cuenta, pero las operaciones de la cartera están pausadas hasta renovar el plan.</p>
                        <Link to="/configuracion/mi-plan" className="k-btn-filled self-start">Ver planes</Link>
                    </section>
                </div>
            </KarteraAdminTemplate>
        )
    }

    return (
        <KarteraAdminTemplate title="Resumen">
            <CustomerDrawer clienteId={model.clienteId} onClose={model.closeCliente} onEdit={model.editCliente} />
            <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title={`Hola, ${model.name}`}
                    description="Así va tu cartera hoy."
                    actions={(
                        <button
                            type="button"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={model.reload}
                        >
                            <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                            Actualizar
                        </button>
                    )}
                />

                <div role="group" aria-label="Ramos" className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        aria-pressed={model.allRamos}
                        onClick={model.selectAllRamos}
                        className={`inline-flex min-h-[40px] items-center rounded-full border px-3.5 text-sm font-semibold ${model.allRamos
                            ? 'border-k-ink bg-k-ink font-bold text-k-surface'
                            : 'border-k-line-strong bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                    >
                        Toda la cartera
                    </button>
                    {SUMMARY_RAMOS.map((ramo) => {
                        const on = !model.allRamos && model.ramos.includes(ramo.id)
                        return (
                            <button
                                key={ramo.id}
                                type="button"
                                aria-pressed={on}
                                onClick={() => model.toggleRamo(ramo.id)}
                                className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3.5 text-sm font-semibold ${on
                                    ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                    : 'border-k-line-strong bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                            >
                                <RamoMark ramo={ramo.ramo} size={16} />
                                {ramo.label}
                            </button>
                        )
                    })}
                </div>

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0 flex-1">{model.error}</p>
                        <button type="button" className="rounded-lg border-0 bg-transparent px-2 py-1 text-sm font-bold text-inherit underline" onClick={model.reload}>Reintentar</button>
                    </div>
                )}

                <div className={`flex flex-col gap-[18px] transition-opacity ${model.loading ? 'opacity-60' : ''}`}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className={cardClass}>
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-tone-purple text-tone-purple-ink"><PeopleAltOutlinedIcon /></span>
                            <span className="flex flex-1 flex-col">
                                <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Clientes</span>
                                <span className="text-[28px] font-extrabold tabular-nums tracking-[-0.03em] text-k-ink">{formatCount(model.clientes)}</span>
                            </span>
                        </div>
                        <div className={cardClass}>
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-tone-blue text-tone-blue-ink"><DescriptionOutlinedIcon /></span>
                            <span className="flex flex-1 flex-col">
                                <span className="text-xs font-bold uppercase tracking-[0.05em] text-k-muted">Pólizas vigentes</span>
                                <span className="text-[28px] font-extrabold tabular-nums tracking-[-0.03em] text-k-ink">{formatCount(model.polizas)}</span>
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2">
                        <AgingCard
                            title="Recibos por cobrar"
                            icon={<PaymentsOutlinedIcon />}
                            to="/cxc"
                            linkLabel="Ver cobranza"
                            enabled={model.can.cobranza}
                            state={model.linkState}
                            buckets={model.cobranza}
                            noun="Recibos pendientes"
                        />
                        <AgingCard
                            title="Pólizas por renovar"
                            icon={<AutorenewIcon />}
                            to="/renovaciones"
                            linkLabel="Ver pólizas"
                            enabled={model.can.renovaciones}
                            state={model.linkState}
                            buckets={model.renovaciones}
                            noun="Pólizas vigentes"
                        />
                    </div>

                    <section className="k-card">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="k-card-title">Avance contra metas</h2>
                            {model.can.metas && (
                                <Link to="/configuracion/metas" state={model.linkState} className="inline-flex items-center gap-1.5 text-sm font-bold text-k-primary-ink hover:underline">
                                    <BarChartOutlinedIcon style={{ fontSize: 18 }} />
                                    Configurar metas
                                </Link>
                            )}
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {model.goals.map((item) => (
                                <div key={item.id} className="flex flex-col items-center gap-2 rounded-2xl border border-k-line-soft bg-k-sunken px-3 pb-4 pt-3 text-center">
                                    <span className="text-[13px] font-bold capitalize text-k-ink-2">{item.title}</span>
                                    <GaugeMeter value={item.progress} label={item.amount} caption={item.goal ? `Meta ${item.goal} · ${item.percent}` : 'Sin meta configurada'} />
                                </div>
                            ))}
                        </div>
                    </section>

                    {model.can.birthdays && (
                        <section className="flex flex-col gap-2 rounded-2xl border border-k-line bg-k-surface px-4 py-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="flex items-center gap-2">
                                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-tone-pink text-tone-pink-ink"><CakeOutlinedIcon style={{ fontSize: 16 }} /></span>
                                    <h2 className="m-0 text-sm font-extrabold text-k-ink">Cumpleañeros de la semana</h2>
                                </span>
                                <span className="text-xs text-k-muted">{model.weekLabel}{model.birthdayCount > 0 ? ` · ${formatCount(model.birthdayCount)}` : ''}</span>
                            </div>
                            {model.birthdaysLoading && model.birthdayDays.length === 0 && (
                                <div className="flex flex-col gap-1.5">
                                    {[1, 2].map((item) => <div key={item} className="h-9 animate-pulse rounded-lg bg-k-line" />)}
                                </div>
                            )}
                            {model.birthdaysError && !model.birthdaysLoading && (
                                <p className="m-0 text-sm text-k-danger-ink">{model.birthdaysError}</p>
                            )}
                            {!model.birthdaysLoading && !model.birthdaysError && model.birthdayDays.length === 0 && (
                                <p className="m-0 text-sm text-k-muted">Ninguno de tus clientes cumple años en los próximos 7 días.</p>
                            )}
                            {model.birthdayDays.length > 0 && (
                                <ul className="m-0 flex list-none flex-col p-0">
                                    {model.birthdayDays.map((day) => (
                                        <li key={day.date} className="flex flex-col gap-1.5 border-t border-k-line-soft py-2 first:border-t-0 sm:flex-row sm:items-center sm:gap-3">
                                            <span className="flex w-32 shrink-0 items-baseline gap-1.5">
                                                <span className={`text-sm font-extrabold ${day.today ? 'rounded-full bg-tone-pink px-2 py-0.5 text-tone-pink-ink' : 'text-k-ink'}`}>{day.label}</span>
                                                <span className="text-xs text-k-muted">{day.dateLabel}</span>
                                            </span>
                                            <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                                                {day.people.map((person) => (
                                                    <BirthdayPerson
                                                        key={person.clienteId}
                                                        person={person}
                                                        onOpen={model.can.clientes ? () => model.openCliente(person.clienteId) : undefined}
                                                    />
                                                ))}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </KarteraAdminTemplate>
    )
}

export default SummaryDashboard
